import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  HelpCircle,
  Layers,
  Moon,
  Search,
  Sun,
  User,
} from 'lucide-react';
import { cn } from '@/shared/cn';
import type { HierarchyLevel, Persona } from '@/shared/types';
import { useTheme, usePersona } from '@/app/providers';
import { defaultRoleForPersona, roleLabel } from '@/versions/v1/shared/RoleContext';
import { DEMO_PROJECTS } from '@/versions/v1/modules/home/Scene.transform';
import { isChatFirstPath } from '@/versions/v1/shared/project-sidebar-nav';
import { Breadcrumb } from './Breadcrumb';
import { ChromeDropLeft } from './ChromeDropLeft';
import { VersionPicker } from './VersionPicker';
import { CommentToggles } from './CommentToggles';
import { replayCommentIntro } from '../comments/CommentIntro';
import { HelpAnchor } from '@/design-system/HelpAnchor';
import { StatusLegendButton } from '@/design-system/StatusLegend';
import { BrandMark } from '@/versions/v1/shared/BrandMark'; // fork(oem): brand-aware home glyph

const PERSONAS: {
  value: Persona;
  label: string;
  locked?: boolean;
  defaultRoute?: string;
}[] = [
  { value: 'Admin', label: 'Admin' },
  { value: 'Editor', label: 'Editor' },
  { value: 'Reviewer', label: 'Reviewer' },
  { value: 'Viewer', label: 'Viewer (read-only)' },
];

function fireOpenPalette() {
  window.dispatchEvent(new CustomEvent('app:open-palette'));
}

function projectFromPath(path: string): string | null {
  const m = path.match(/^\/projects\/([^/?]+)/);
  return m ? m[1] : null;
}

const CHROME_ICON = 'theme-toggle tt-left';

export function TopBar({ level }: { level: HierarchyLevel }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { persona, setPersona } = usePersona();

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);

  const switcherRef = useRef<HTMLDivElement | null>(null);
  const personaRef = useRef<HTMLDivElement | null>(null);
  const profileRef = useRef<HTMLDivElement | null>(null);
  const contextRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (switcherRef.current && !switcherRef.current.contains(target)) setSwitcherOpen(false);
      if (personaRef.current && !personaRef.current.contains(target)) setPersonaOpen(false);
      if (profileRef.current && !profileRef.current.contains(target)) setProfileOpen(false);
      if (contextRef.current && !contextRef.current.contains(target)) setContextOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (level === 'auth') return null;

  const chatFirst = isChatFirstPath(location.pathname);
  const isProjectLevel = level === 'project' || level === 'entity';
  const projectSlug = projectFromPath(location.pathname);
  const project =
    DEMO_PROJECTS.find((p) => p.slug === projectSlug) ?? DEMO_PROJECTS[0];

  const moduleSegment = location.pathname.split('/')[3] ?? '';
  const moduleLabel = moduleSegment
    ? moduleSegment.charAt(0).toUpperCase() + moduleSegment.slice(1)
    : '';
  const breadcrumb = isProjectLevel
    ? [
        { label: project.name, hash: `/projects/${project.slug}` },
        ...(moduleLabel ? [{ label: moduleLabel, static: true }] : []),
      ]
    : [];
  const workspaceBreadcrumb =
    location.pathname === '/changelog'
      ? [{ label: 'Changelog', hash: '/changelog' }]
      : location.pathname.startsWith('/docs/logic')
        ? [{ label: 'Build docs', hash: '/docs/logic' }]
        : [{ label: 'Home', hash: '/' }];

  const showNavCluster = !chatFirst;

  return (
    <aside
      data-comment-anchor="TopBar"
      className="top-chrome vertical-chrome z-40 flex flex-col items-center py-3"
      aria-label="Review chrome"
    >
      {showNavCluster ? (
        <div className="flex w-full flex-col items-center gap-1 px-1">
          <button
            type="button"
            data-comment-nav="true"
            onClick={() => navigate('/')}
            className={CHROME_ICON}
            aria-label="Home"
          >
            <BrandMark variant="mark" /> {/* fork(oem): was <Box … /> */}
          </button>

          {isProjectLevel ? (
            <div className="relative" ref={switcherRef}>
              <button
                type="button"
                className={CHROME_ICON}
                onClick={() => setSwitcherOpen((o) => !o)}
                aria-haspopup="true"
                aria-expanded={switcherOpen}
                aria-label={`Switch project · ${project.name}`}
              >
                <Layers size={16} style={{ color: 'var(--ink-secondary)' }} aria-hidden="true" />
              </button>
              {switcherOpen ? (
                <ChromeDropLeft
                  open={switcherOpen}
                  anchorRef={switcherRef}
                  style={{ width: '240px', padding: '6px' }}
                >
                  {DEMO_PROJECTS.map((p) => (
                    <button
                      key={p.slug}
                      type="button"
                      data-comment-nav="true"
                      className="persona-row"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'left',
                      }}
                      onClick={() => {
                        setSwitcherOpen(false);
                        navigate(`/projects/${p.slug}`);
                      }}
                    >
                      <span>{p.name}</span>
                    </button>
                  ))}
                </ChromeDropLeft>
              ) : null}
            </div>
          ) : null}

          <div className="relative" ref={contextRef}>
            <button
              type="button"
              className={cn(CHROME_ICON, 'text-[10px] font-semibold uppercase tracking-wider')}
              onClick={() => setContextOpen((o) => !o)}
              aria-haspopup="true"
              aria-expanded={contextOpen}
              aria-label="Page context"
              style={{ color: 'var(--ink-tertiary)', width: 28, height: 28 }}
            >
              ···
            </button>
            {contextOpen ? (
              <ChromeDropLeft
                open={contextOpen}
                anchorRef={contextRef}
                style={{ width: 'min(280px, 70vw)', padding: '10px 12px' }}
              >
                <Breadcrumb segments={isProjectLevel ? breadcrumb : workspaceBreadcrumb} />
              </ChromeDropLeft>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="flex-1" aria-hidden="true" />

      <div className="flex w-full flex-col items-center gap-1.5 px-1">
        <button
          type="button"
          onClick={fireOpenPalette}
          className={CHROME_ICON}
          aria-label="Search · ⌘K"
        >
          <Search size={16} aria-hidden="true" />
        </button>

        <button
          type="button"
          className={CHROME_ICON}
          onClick={toggleTheme}
          aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
        >
          {theme === 'light' ? (
            <Sun size={16} aria-hidden="true" />
          ) : (
            <Moon size={16} aria-hidden="true" />
          )}
        </button>

        <div className="relative" ref={personaRef}>
          <button
            type="button"
            className={cn(CHROME_ICON, 'persona-chip-icon')}
            onClick={() => setPersonaOpen((o) => !o)}
            aria-haspopup="true"
            aria-expanded={personaOpen}
            aria-label={`Viewing as ${persona}`}
            style={{ width: 28, height: 28, borderRadius: 999, padding: 0 }}
          >
            <span
              className="tabular"
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.04em',
                color: 'var(--ink-primary)',
              }}
            >
              {persona.slice(0, 1)}
            </span>
          </button>
          {personaOpen ? (
            <ChromeDropLeft
              open={personaOpen}
              anchorRef={personaRef}
              className="persona-dropdown"
              data-comment-anchor="PersonaSwitcher"
            >
              <div
                className="flex items-center justify-between"
                style={{
                  padding: '6px 10px 4px 10px',
                  borderBottom: '1px solid var(--hairline)',
                  marginBottom: 4,
                }}
              >
                <span
                  className="tabular"
                  style={{
                    fontSize: 9,
                    color: 'var(--ink-tertiary)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                  }}
                >
                  Switch persona
                </span>
                <HelpAnchor
                  doc="workspace/roles-and-capabilities"
                  label="Roles and capabilities"
                />
              </div>
              {PERSONAS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  className={['persona-row', p.locked ? 'locked' : ''].filter(Boolean).join(' ')}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                  }}
                  onClick={() => {
                    if (p.locked) return;
                    setPersona(p.value);
                    setPersonaOpen(false);
                    if (p.defaultRoute) {
                      navigate(p.defaultRoute);
                    }
                  }}
                >
                  <span style={{ display: 'block' }}>{p.label}</span>
                  <span
                    className="tabular"
                    style={{
                      display: 'block',
                      marginTop: 2,
                      fontSize: 10,
                      color: 'var(--ink-tertiary)',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Role · {roleLabel(defaultRoleForPersona(p.value))}
                  </span>
                </button>
              ))}
            </ChromeDropLeft>
          ) : null}
        </div>

        <div className="relative" ref={profileRef}>
          <button
            type="button"
            className={CHROME_ICON}
            onClick={() => setProfileOpen((o) => !o)}
            aria-label="Account menu"
            aria-haspopup="true"
            aria-expanded={profileOpen}
          >
            <User size={16} aria-hidden="true" />
          </button>
          {profileOpen ? (
            <ChromeDropLeft
              open={profileOpen}
              anchorRef={profileRef}
              style={{ width: '180px', padding: '6px' }}
            >
              <button
                type="button"
                className="persona-row"
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  background: 'transparent',
                }}
                onClick={() => {
                  setProfileOpen(false);
                  navigate('/settings/account');
                }}
              >
                Account
              </button>
              <button
                type="button"
                data-comment-nav="true"
                data-review-mode="true"
                className="persona-row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  background: 'transparent',
                }}
                onClick={() => {
                  setProfileOpen(false);
                  navigate('/docs/logic');
                }}
              >
                <BookOpen size={14} strokeWidth={1.75} />
                Build docs
              </button>
              <button
                type="button"
                data-comment-nav="true"
                className="persona-row"
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  background: 'transparent',
                }}
                onClick={() => {
                  setProfileOpen(false);
                  navigate('/login?reason=session-ended');
                }}
              >
                Sign out
              </button>
            </ChromeDropLeft>
          ) : null}
        </div>

        <CommentToggles vertical />

        <StatusLegendButton tooltipClass="tt-left" />

        <button
          type="button"
          onClick={replayCommentIntro}
          className={CHROME_ICON}
          aria-label="Show how to leave feedback"
          data-comment-ui="true"
          data-review-mode="true"
        >
          <HelpCircle size={16} aria-hidden="true" />
        </button>

        <VersionPicker compact />
      </div>
    </aside>
  );
}
